import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { X, Loader2, Copy, Download, ExternalLink, AlertTriangle, XCircle, Info } from 'lucide-react';
import { useRunActivityLog, useRunRawCalls } from '../../hooks/control-testing/index.ts';
import { filterEntries, formatDuration, formatMs, collectCalls, pairLabel, logToText, tokensLabel, collectSources, collectBackendSteps, countStalls } from './runLogUtils';

const LEVEL_STYLE = {
  info: 'text-gray-600',
  warning: 'text-amber-700 bg-amber-50',
  error: 'text-red-700 bg-red-50',
};

const OUTCOME_STYLE = {
  completed: 'bg-green-50 text-green-700',
  failed: 'bg-red-50 text-red-700',
  cancelled: 'bg-amber-50 text-amber-700',
  persist_failed: 'bg-red-50 text-red-700',
};

function LevelIcon({ level }) {
  if (level === 'error') return <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0 mt-0.5" />;
  if (level === 'warning') return <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />;
  return <Info className="h-3.5 w-3.5 text-gray-300 shrink-0 mt-0.5" />;
}

function copy(text, what) {
  try {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${what} copied`),
      () => toast.error('Could not copy to the clipboard'),
    );
  } catch {
    toast.error('Could not copy to the clipboard');
  }
}

function UrlChip({ url }) {
  if (!url?.url) return null;
  const bad = url.status == null || url.status >= 400;
  return (
    <span className="inline-flex items-center gap-1 ml-1 align-middle">
      <button
        type="button"
        onClick={() => copy(url.url, 'URL')}
        title="Copy URL"
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-[11px] font-mono text-gray-700 max-w-[320px]"
      >
        <span className="font-semibold">{url.method}</span>
        <span className="truncate">{url.url}</span>
        <Copy className="h-3 w-3 shrink-0" />
      </button>
      {(url.status != null || url.duration_ms != null) && (
        <span className={`text-[11px] ${bad ? 'text-red-600' : 'text-gray-500'}`}>
          {url.status ?? 'error'}{url.duration_ms != null ? ` · ${formatMs(url.duration_ms)}` : ''}
        </span>
      )}
    </span>
  );
}

function Timeline({ entries }) {
  if (!entries.length) return <p className="text-sm text-gray-500 py-6 text-center">Nothing to show for this filter.</p>;
  return (
    <ul className="divide-y divide-gray-100">
      {entries.map((e, i) => (
        <li key={i} className={`px-3 py-1.5 text-xs flex gap-2 ${LEVEL_STYLE[e.level] || ''}`}>
          <LevelIcon level={e.level} />
          <span className="font-mono text-gray-400 shrink-0 w-14">{formatDuration(e.t)}</span>
          <span className="shrink-0 w-20 text-indigo-600">{e.stage}</span>
          <span className="min-w-0 break-words">
            {e.kind === 'http_call' && e.url
              ? <span className="text-gray-500">{e.url.target}{e.detail?.phase ? ` · ${e.detail.phase}` : ''}{e.detail?.model ? ` · ${e.detail.model}` : ''}</span>
              : e.message}
            <UrlChip url={e.url} />
            {e.detail?.error && <span className="block text-red-600">{e.detail.error}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Kv({ label, value }) {
  if (value === undefined || value === null || value === '') return null;
  return <div className="flex gap-2"><span className="text-gray-400 w-36 shrink-0">{label}</span><span className="font-mono break-all">{String(value)}</span></div>;
}

function PairRow({ p, onOpenResults }) {
  const [open, setOpen] = useState(false);
  const bad = p.stage1_outcome && p.stage1_outcome !== 'ok';
  const bad2 = p.stage2_outcome && p.stage2_outcome !== 'ok';
  return (
    <>
      <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setOpen((v) => !v)}>
        <td className="px-3 py-2 font-medium text-gray-800">{pairLabel(p)}</td>
        <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded text-[11px] ${p.outcome === 'passed' ? 'bg-green-50 text-green-700' : p.outcome === 'failed' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>{p.outcome || 'no result'}</span></td>
        <td className={`px-3 py-2 ${bad ? 'text-red-600' : ''}`}>{formatDuration(p.stage1_s)}{bad ? ` (${p.stage1_outcome})` : ''}</td>
        <td className={`px-3 py-2 ${bad2 ? 'text-red-600' : ''}`}>{formatDuration(p.stage2_s)}{bad2 ? ` (${p.stage2_outcome})` : ''}</td>
        <td className="px-3 py-2">{p.items_listed ?? p.items ?? '—'}{p.items_declared != null ? ` / ${p.items_declared}` : ''}</td>
        <td className="px-3 py-2">{p.calls?.length || 0}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6} className="px-4 py-3 bg-gray-50 text-xs space-y-1">
            <Kv label="Waited for a slot" value={p.waited_s != null ? formatDuration(p.waited_s) : ''} />
            <Kv label="Analysis model" value={p.stage1_model} />
            <Kv label="Analysis turns" value={p.stage1_turns} />
            <Kv label="Continuations" value={p.stage1_continuations} />
            <Kv label="Analysis output chars" value={p.stage1_chars} />
            <Kv label="Analysis error" value={p.stage1_error} />
            <Kv label="Parser model" value={p.stage2_model} />
            <Kv label="Parser input chars" value={p.stage2_input_chars} />
            <Kv label="Parser attempts" value={p.stage2_attempts} />
            <Kv label="Parser fallback used" value={p.stage2_fallback ? 'yes (analyzer status and items kept)' : ''} />
            <Kv label="Parser error" value={p.stage2_error} />
            <Kv label="Status from parser" value={p.status_parser} />
            <Kv label="Status after items" value={p.status_after_items} />
            <Kv label="Items failed" value={p.items_failed} />
            <Kv label="Result" value={p.observation} />
            {p.calls?.length > 0 && (
              <div className="pt-2">
                <div className="text-gray-500 mb-1">Calls</div>
                {p.calls.map((c, i) => (
                  <div key={i} className="font-mono text-[11px]">
                    {c.target} · {c.phase ? `${c.phase} · ` : ''}{c.method} {c.url} → {c.status ?? 'error'} · {formatMs(c.duration_ms)}
                    {c.attempt ? ` · attempt ${c.attempt}` : ''}
                    {c.input_tokens != null ? ` · ${c.input_tokens} in / ${c.output_tokens} out tokens` : ''}
                    {c.request_id ? ` · ${c.request_id}` : ''}
                    {c.error ? <span className="text-red-600"> · {c.error}</span> : null}
                  </div>
                ))}
              </div>
            )}
            {onOpenResults && (
              <button type="button" onClick={onOpenResults} className="inline-flex items-center gap-1 mt-2 text-indigo-600 hover:underline">
                <ExternalLink className="h-3 w-3" /> Open results
              </button>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

const RAW_LABEL = { analysis: 'Analysis', parse: 'Parse', summary: 'Executive summary' };

function RawField({ label, text }) {
  if (!text) return null;
  return (
    <div className="mt-2">
      <div className="flex items-center gap-2 text-[11px] text-gray-500">
        <span className="uppercase tracking-wide">{label}</span>
        <button type="button" onClick={() => copy(text, label)} className="inline-flex items-center gap-1 hover:text-gray-700"><Copy className="h-3 w-3" /> Copy</button>
      </div>
      <pre className="mt-0.5 max-h-72 overflow-auto whitespace-pre-wrap break-words rounded bg-gray-50 border border-gray-200 p-2 text-[11px] leading-snug">{text}</pre>
    </div>
  );
}

function RawCalls({ runId, recorded }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading, isError, error } = useRunRawCalls(runId, open);
  const calls = data?.calls || [];
  if (!recorded) {
    return <p className="text-sm text-gray-500">Not recorded for this run. Tick "Record raw model calls" on the Test Runs tab before running to keep the prompts, tool output and responses.</p>;
  }
  return (
    <div>
      <button type="button" onClick={() => setOpen((v) => !v)} className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50">
        {open ? 'Hide raw model calls' : `Show raw model calls (${recorded})`}
      </button>
      <p className="mt-1 text-[11px] text-amber-700">These contain evidence text and are kept for 14 days.</p>
      {open && isLoading && <Loader2 className="mt-3 h-4 w-4 animate-spin text-indigo-600" />}
      {open && isError && <p className="mt-2 text-sm text-red-700">Could not load the raw calls{error?.message ? `: ${error.message}` : ''}.</p>}
      {open && !isLoading && !isError && calls.length === 0 && <p className="mt-2 text-sm text-gray-500">The raw calls are no longer available (they are kept for 14 days).</p>}
      {open && calls.map((c, i) => (
        <details key={i} className="mt-2 border border-gray-200 rounded">
          <summary className="cursor-pointer px-3 py-1.5 text-xs flex flex-wrap gap-x-3 gap-y-0.5">
            <span className="font-semibold">{RAW_LABEL[c.kind] || c.kind}</span>
            {c.sample_ref && <span>{c.sample_ref} · {c.attr_ref}</span>}
            <span className="font-mono text-gray-500">{c.model}</span>
            <span className="text-gray-400">{formatDuration(c.t)}</span>
            {c.stop_reason && <span className="text-gray-500">{c.stop_reason}</span>}
            {c.truncated && <span className="text-amber-700">cut at 20,000 characters</span>}
          </summary>
          <div className="px-3 pb-3">
            <RawField label="System prompt" text={c.system} />
            <RawField label="Request" text={c.request} />
            <RawField label="Response" text={c.response} />
          </div>
        </details>
      ))}
    </div>
  );
}

function Debug({ runId, summary, entries, onOpenResults }) {
  const pairs = summary?.pairs || [];
  const calls = useMemo(() => collectCalls(entries), [entries]);
  const sources = useMemo(() => collectSources(entries), [entries]);
  const backendSteps = useMemo(() => collectBackendSteps(entries), [entries]);
  const config = summary?.config || {};
  return (
    <div className="space-y-5 p-3">
      <section>
        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Checks (sample × attribute)</h4>
        {pairs.length === 0 ? (
          <p className="text-sm text-gray-500">No per-check detail was recorded.</p>
        ) : (
          <div className="overflow-x-auto border border-gray-200 rounded">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-500 text-left">
                <tr><th className="px-3 py-2">Check</th><th className="px-3 py-2">Result</th><th className="px-3 py-2">Analysis</th><th className="px-3 py-2">Parse</th><th className="px-3 py-2">Items</th><th className="px-3 py-2">Calls</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pairs.map((p) => <PairRow key={`${p.sample_ref}||${p.attr_ref}`} p={p} onOpenResults={onOpenResults} />)}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section>
        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Evidence: where files came from</h4>
        {sources.length === 0 ? (
          <p className="text-sm text-gray-500">No source detail was recorded.</p>
        ) : (
          <ul className="border border-gray-200 rounded divide-y divide-gray-100 text-xs">
            {sources.map((e, i) => (
              <li key={i} className={`px-3 py-1.5 ${e.level === 'warning' ? 'bg-amber-50 text-amber-800' : ''}`}>
                {e.message}
                {e.url?.url && <span className="block font-mono text-[11px] text-gray-500 break-all">{e.url.url}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>
      {backendSteps.length > 0 && (
        <section>
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Backend: loading the run</h4>
          <div className="border border-gray-200 rounded">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-3 py-2">Step</th><th className="px-3 py-2">Time</th><th className="px-3 py-2">Rows</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {backendSteps.map((b, i) => (
                  <tr key={i} className={b.duration_ms >= 500 ? 'bg-amber-50' : ''}><td className="px-3 py-1.5 font-mono">{b.step}</td><td className="px-3 py-1.5">{formatMs(b.duration_ms)}</td><td className="px-3 py-1.5">{b.rows ?? '—'}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      <section>
        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">All calls, slowest first ({calls.length})</h4>
        {calls.length === 0 ? (
          <p className="text-sm text-gray-500">No calls were recorded.</p>
        ) : (
          <div className="overflow-x-auto border border-gray-200 rounded">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-500 text-left">
                <tr><th className="px-3 py-2">Target</th><th className="px-3 py-2">Call</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Time</th><th className="px-3 py-2">Tokens</th><th className="px-3 py-2">Check</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {calls.slice(0, 200).map((c, i) => (
                  <tr key={i} className={c.status == null || c.status >= 400 ? 'bg-red-50' : ''}>
                    <td className="px-3 py-1.5">{c.target}{c.phase ? ` · ${c.phase}` : ''}</td>
                    <td className="px-3 py-1.5 font-mono"><button type="button" title="Copy URL" onClick={() => copy(c.url, 'URL')} className="text-left hover:underline">{c.method} {c.url}</button>{c.error ? <span className="block text-red-600">{c.error}</span> : null}</td>
                    <td className="px-3 py-1.5">{c.status ?? 'error'}</td>
                    <td className="px-3 py-1.5">{formatMs(c.duration_ms)}</td>
                    <td className="px-3 py-1.5 text-gray-500">{tokensLabel(c.detail) || (c.detail?.bytes != null ? `${c.detail.bytes} B` : '')}</td>
                    <td className="px-3 py-1.5">{c.sample_ref ? `${c.sample_ref} · ${c.attr_ref}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section>
        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Raw model calls</h4>
        <RawCalls runId={runId} recorded={summary?.raw_calls?.count || 0} />
      </section>
      <section>
        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Settings in effect</h4>
        <div className="text-xs space-y-0.5">
          {Object.keys(config).length === 0 ? <p className="text-gray-500">Not recorded.</p> : Object.entries(config).map(([k, v]) => <Kv key={k} label={k} value={v === '' ? '(unset)' : v} />)}
        </div>
      </section>
    </div>
  );
}

/**
 * The saved activity log of a finished run: Timeline (what the live log showed) and Debug (per-check
 * timings, every call with its cleaned URL, and the settings in effect).
 */
export default function RunLogModal({ runId, runName, onClose, onOpenResults }) {
  const { data: log, isLoading, isError, error, refetch } = useRunActivityLog(runId);
  const [tab, setTab] = useState('timeline');
  const [level, setLevel] = useState('all');
  const entries = useMemo(() => filterEntries(log?.entries, level), [log, level]);
  const s = log?.summary || {};

  const download = () => {
    try {
      const blob = new Blob([JSON.stringify(log, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `run-log-${runId}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error('Could not download the log');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-label="Run activity log">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[88vh] flex flex-col overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2 shrink-0">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900">Activity log</div>
            <div className="text-xs text-gray-500 truncate">{runName}</div>
          </div>
          {log && (
            <>
              <button type="button" onClick={() => copy(logToText(log), 'Log')} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"><Copy className="h-3.5 w-3.5" /> Copy</button>
              <button type="button" onClick={download} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"><Download className="h-3.5 w-3.5" /> Download</button>
            </>
          )}
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
        </div>

        {isLoading && <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-600" /></div>}

        {isError && (
          <div className="p-6 text-sm text-red-700">
            Could not load the activity log{error?.message ? `: ${error.message}` : ''}.{' '}
            <button type="button" className="underline" onClick={() => refetch()}>Try again</button>
          </div>
        )}

        {!isLoading && !isError && !log && (
          <div className="p-8 text-sm text-gray-600 text-center">No log was recorded for this run. Runs executed before the activity log existed do not have one.</div>
        )}

        {log && (
          <>
            <div className="px-4 py-2 border-b border-gray-100 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
              <span className={`px-1.5 py-0.5 rounded ${OUTCOME_STYLE[log.outcome] || 'bg-gray-100'}`}>{String(log.outcome || '').replace('_', ' ')}</span>
              <span>Duration {formatDuration(s.duration_s)}</span>
              <span>{s.entry_count ?? log.entries?.length ?? 0} entries</span>
              <span className="text-amber-700">{s.levels?.warning || 0} warnings</span>
              <span className="text-red-700">{s.levels?.error || 0} errors</span>
              {s.pair_outcomes && Object.entries(s.pair_outcomes).map(([k, v]) => <span key={k}>{v} {k}</span>)}
              {s.config?.parser_model && <span className="font-mono">parser: {s.config.parser_model}</span>}
              {countStalls(log.entries) > 0 && <span className="text-amber-700">{countStalls(log.entries)} service stall(s)</span>}
              {s.dropped_entries > 0 && <span className="text-amber-700">{s.dropped_entries} entries dropped (log cap)</span>}
              {onOpenResults && <button type="button" onClick={onOpenResults} className="ml-auto inline-flex items-center gap-1 text-indigo-600 hover:underline"><ExternalLink className="h-3 w-3" /> Open results</button>}
            </div>
            <div className="px-4 pt-2 flex items-center gap-1 border-b border-gray-200">
              {['timeline', 'debug'].map((t) => (
                <button key={t} type="button" onClick={() => setTab(t)} className={`px-3 py-1.5 text-sm border-b-2 -mb-px capitalize ${tab === t ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{t}</button>
              ))}
              {tab === 'timeline' && (
                <select value={level} onChange={(e) => setLevel(e.target.value)} aria-label="Filter" className="ml-auto mb-1 text-xs border border-gray-200 rounded px-2 py-1">
                  <option value="all">All</option>
                  <option value="warning">Warnings and errors</option>
                  <option value="error">Errors only</option>
                </select>
              )}
            </div>
            <div className="overflow-y-auto flex-1">
              {tab === 'timeline' ? <Timeline entries={entries} /> : <Debug runId={runId} summary={s} entries={log.entries} onOpenResults={onOpenResults} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
